import type { ComponentType, ReactNode } from 'react';
import { cn } from '@/shared/lib';
import { Badge } from '@/shared/ui/badge';
import type { DetailAdjacent, DetailStatus } from './types';
import { styles } from './styles';

/**
 * The commerce-style header: an elevated card with title, inline status +
 * extra badges, and the tab strip inside the card footer.
 *
 * Breadcrumbs and page actions are NOT rendered here — the orchestrator
 * renders them as a strip ABOVE this card. The card itself stays focused on
 * the title + tabs.
 *
 * `primaryAction` / `secondaryAction` are accepted on the type for backward
 * compatibility but are no longer rendered inside this card. The orchestrator
 * places them in the row above.
 */
export function AccentHeader({
  title,
  subtitle,
  status,
  badges,
  prev,
  next,
  tabs,
  activeTabId,
  onTabChange,
  tabsInHeader,
  PrevNextSlot,
}: AccentHeaderProps): ReactNode {
  const tabsElement = buildTabsFooter(tabs, tabsInHeader, activeTabId, onTabChange);
  const prevNextElement = buildPrevNext(prev, next, PrevNextSlot);
  return (
    <div className={styles.accentCard}>
      <div className={styles.accentHeaderBody}>
        <AccentTitleBlock title={title} subtitle={subtitle} status={status} badges={badges} />
        {prevNextElement}
      </div>
      {tabsElement}
    </div>
  );
}

function buildTabsFooter(
  tabs: ReadonlyArray<DetailTabDescriptor> | undefined,
  tabsInHeader: boolean | undefined,
  activeTabId: string | undefined,
  onTabChange: ((id: string) => void) | undefined,
): ReactNode {
  if (!tabsInHeader || !tabs || tabs.length === 0) return null;
  return (
    <AccentHeaderTabs
      tabs={tabs}
      activeTabId={activeTabId ?? tabs[0]?.id ?? ''}
      onTabChange={onTabChange ?? (() => undefined)}
    />
  );
}

function buildPrevNext(
  prev: DetailAdjacent | undefined,
  next: DetailAdjacent | undefined,
  PrevNextSlot: ComponentType<{ prev: DetailAdjacent; next: DetailAdjacent }> | undefined,
): ReactNode {
  if (!PrevNextSlot) return null;
  if (!prev && !next) return null;
  return <PrevNextSlot prev={prev ?? null} next={next ?? null} />;
}

/** Title row: the title with the status + extra badges inline, subtitle below. */
function AccentTitleBlock({
  title,
  subtitle,
  status,
  badges,
}: {
  title: string;
  subtitle?: string;
  status?: DetailStatus;
  badges?: ReadonlyArray<ReactNode>;
}): ReactNode {
  return (
    <div className={styles.accentHeaderText}>
      <h1 className={styles.accentTitle}>
        <span>{title}</span>
        {status ? (
          <Badge tone={status.tone} className={styles.accentBadge}>
            {status.label}
          </Badge>
        ) : null}
        {badges?.map((badge, index) => (
          <span key={index} className={styles.accentBadge}>
            {badge}
          </span>
        ))}
      </h1>

      {subtitle ? <p className={styles.accentSubtitle}>{subtitle}</p> : null}
    </div>
  );
}

/** The tab strip rendered inside the accent card footer. */
function AccentHeaderTabs({
  tabs,
  activeTabId,
  onTabChange,
}: {
  tabs: ReadonlyArray<DetailTabDescriptor>;
  activeTabId: string;
  onTabChange: (id: string) => void;
}): ReactNode {
  return (
    <div className={styles.tabsFooter}>
      <div role="tablist" aria-label="Record sections" className={styles.tabsFooterList}>
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`${tab.id}-tab`}
              aria-controls={`${tab.id}-panel`}
              aria-selected={isActive}
              onClick={() => onTabChange(tab.id)}
              className={cn(
                styles.tabsFooterTab,
                isActive ? styles.tabsFooterTabActive : styles.tabsFooterTabInactive,
              )}
            >
              {tab.icon ? (
                <span aria-hidden="true" className={styles.tabsFooterIcon}>
                  {tab.icon}
                </span>
              ) : null}
              <span>{tab.label}</span>
              {typeof tab.count === 'number' ? (
                <span
                  className={cn(
                    styles.tabsFooterCount,
                    isActive ? styles.tabsFooterCountActive : styles.tabsFooterCountInactive,
                  )}
                >
                  {tab.count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

type DetailTabDescriptor = {
  readonly id: string;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly count?: number;
};

type AccentHeaderProps = {
  title: string;
  subtitle?: string;
  status?: DetailStatus;
  badges?: ReadonlyArray<ReactNode>;
  prev?: DetailAdjacent;
  next?: DetailAdjacent;
  // Kept on the type for backward compatibility but rendered by the
  // orchestrator above the card, not inside this header.
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  tabs?: ReadonlyArray<DetailTabDescriptor>;
  activeTabId?: string;
  onTabChange?: (id: string) => void;
  tabsInHeader?: boolean;
  PrevNextSlot?: ComponentType<{ prev: DetailAdjacent; next: DetailAdjacent }>;
};